<template>
  <div class="pb-page tv">
    <PageHeader title="Testimonials" subtitle="What's ready to publish, and what's live on your widgets and hosted wall.">
      <router-link to="/testimonials/import" class="pb-button outline"><i class="mdi mdi-import pb-ico" aria-hidden="true"></i>Import existing testimonials</router-link>
    </PageHeader>

    <SkeletonList v-if="loading" :rows="4" label="Loading testimonials…" />
    <div v-else-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span><button type="button" class="pb-button sm outline" @click="load">Retry</button></div>
    <EmptyState v-else-if="!ready.length && !published.length" icon="mdi-comment-quote-outline" title="No testimonials yet"
                text="Create a collection page or import existing testimonials.">
      <router-link to="/forms" class="pb-button">Create collection page</router-link>
      <router-link to="/testimonials/import" class="pb-button outline">Import</router-link>
    </EmptyState>

    <template v-else>
      <section class="tv-section" aria-labelledby="tv-ready">
        <div class="pb-card-head stacks">
          <div><h2 id="tv-ready" class="pb-section-title display-font">Ready to publish <span class="tv-n">{{ ready.length }}</span></h2>
            <p class="pb-muted">Approved{{ brand && brand.requires_client_approval ? ' by you and your client' : '' }}, with permission to publish: the customer's own for responses, your confirmation for imports.</p></div>
          <template v-if="ready.length > 1">
            <LockedAction v-if="publishCap" feature="published" look="pb-button" :label="'Publish all ' + ready.length" :cap="publishCap" :used="usage.published || 0" />
            <button v-else type="button" class="pb-button" :disabled="busy" @click="publishAll">Publish all {{ ready.length }}</button>
          </template>
        </div>
        <p v-if="!ready.length" class="pb-card flat tv-none">Nothing waiting. Approve feedback in the <router-link to="/inbox?status=in_review">inbox</router-link> and it appears here.</p>
        <ul v-else class="tv-list">
          <li v-for="row in ready" :key="row.id" class="tv-item">
            <div class="tv-body">
              <p class="tv-text">{{ row.excerpt }}</p>
              <p class="pb-muted">{{ who(row) }}</p>
            </div>
            <div class="pb-actions tv-actions">
              <router-link :to="'/inbox/' + row.id" class="pb-button sm ghost">Open</router-link>
              <LockedAction v-if="publishCap" feature="published" look="pb-button sm" label="Publish" :used="usage.published || 0" />
              <button v-else type="button" class="pb-button sm" :disabled="busy" @click="act(row, 'publish')">Publish</button>
            </div>
          </li>
        </ul>
      </section>

      <section class="tv-section" aria-labelledby="tv-live">
        <div class="pb-card-head tv-live-head">
          <div><h2 id="tv-live" class="pb-section-title display-font">Published <span class="tv-n">{{ published.length }}</span></h2>
            <p class="pb-muted">Drag to set the order used by widgets and walls set to "manual order".</p></div>
          <div class="tv-meter"><LimitMeter :label="scopedToBrands ? 'Published across your brands' : 'Published across your workspace'" :used="usage.published || 0" :allowed="limits.published === undefined ? null : limits.published" /></div>
        </div>
        <p v-if="!published.length" class="pb-card flat tv-none">Nothing published yet.</p>
        <ol v-else class="tv-list" aria-label="Published testimonials in manual order">
          <li v-for="(row, index) in published" :key="row.id" :class="['tv-item', 'live', { dragging: dragId === row.id, over: overId === row.id }]"
              draggable="true" @dragstart="dragStart(row, $event)" @dragover.prevent="overId = row.id" @dragleave="overId = null"
              @drop.prevent="drop(row)" @dragend="dragId = null; overId = null">
            <span class="tv-grip" aria-hidden="true"><i class="mdi mdi-drag-vertical"></i></span>
            <span class="tv-pos">{{ index + 1 }}</span>
            <div class="tv-body">
              <p class="tv-text">{{ row.excerpt }}</p>
              <p class="pb-muted">{{ who(row) }}</p>
              <p v-if="row.public && !row.public.showing" class="tv-hidden"><span class="pb-chip is-warning"><i class="mdi mdi-eye-off-outline pb-ico" aria-hidden="true"></i>Not showing publicly</span>
                <span class="pb-muted">{{ row.public.reason }}</span></p>
            </div>
            <div class="pb-actions tv-actions">
              <button type="button" class="pb-icon-button" :disabled="index === 0" :aria-label="'Move up: ' + (row.name || 'testimonial ' + (index + 1))" @click="shift(index, -1)"><i class="mdi mdi-arrow-up" aria-hidden="true"></i></button>
              <button type="button" class="pb-icon-button" :disabled="index === published.length - 1" :aria-label="'Move down: ' + (row.name || 'testimonial ' + (index + 1))" @click="shift(index, 1)"><i class="mdi mdi-arrow-down" aria-hidden="true"></i></button>
              <router-link :to="'/inbox/' + row.id" class="pb-button sm ghost">Open</router-link>
              <button type="button" class="pb-button sm outline" :disabled="busy" @click="act(row, 'unpublish')">Unpublish</button>
            </div>
          </li>
        </ol>
      </section>
    </template>
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import EmptyState from '../components/EmptyState.vue';
import LimitMeter from '../components/LimitMeter.vue';
import PageHeader from '../components/PageHeader.vue';
import SkeletonList from '../components/SkeletonList.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import LockedAction from '../components/LockedAction.vue';
import workspace from '../mixins/workspace';
import { get, post, message, planLimit } from '../api';
import { formatDate } from '../lib/format';
import { publishedToast } from '../lib/showing';
import { toast } from '../lib/toast';

export default {
  name: 'TestimonialsView',
  components: { EmptyState, LimitMeter, LockedAction, PageHeader, SkeletonList, UpgradeDialog },
  mixins: [workspace],
  data: () => ({ ready: [], published: [], loading: true, error: '', busy: false, dragId: null, overId: null, upgrade: null, upgradeOpen: false }),
  computed: {
    // At the plan's cap, with a plan above that publishes more: Publish shows the cap (lib/plans.js) instead of failing.
    publishCap() { return this.capOf('published', this.usage.published || 0); }
  },
  watch: { brandId() { this.load(); } },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.load();
  },
  methods: {
    who(row) {
      const parts = [[row.name || 'Anonymous', row.company].filter(Boolean).join(', ')];
      if (row.source_label) parts.push(row.source_label);
      if (row.published_at) parts.push('published ' + formatDate(row.published_at));
      return parts.join(' · ');
    },
    async load() {
      if (!this.brandId) return;
      this.loading = true;
      this.error = '';
      try {
        const [ready, live] = await Promise.all([
          get('/brands/' + this.brandId + '/submissions', { status: 'approved' }),
          get('/brands/' + this.brandId + '/submissions', { status: 'published', order: 'manual' })
        ]);
        this.ready = ready.items;
        this.published = live.items;
      } catch (error) { this.error = message(error); } finally { this.loading = false; }
    },
    limited(error) {
      const limit = planLimit(error);
      if (limit) { this.upgrade = limit; this.upgradeOpen = true; return true; }
      toast(message(error), 'error');
      return false;
    },
    async act(row, action) {
      this.busy = true;
      try {
        await post('/submissions/' + row.id + '/actions', { action });
        if (action === 'publish') {
          const done = publishedToast(this.brand, 1);
          toast(done.text, 'success', done.action);
        } else toast('Unpublished. It leaves your widget and wall within a minute.');
        this.refreshWorkspace();
        await this.load();
      } catch (error) { this.limited(error); } finally { this.busy = false; }
    },
    async publishAll() {
      this.busy = true;
      try {
        const result = await post('/submissions/actions', { ids: this.ready.map(row => row.id), action: 'publish' });
        if (result.done.length) {
          const done = publishedToast(this.brand, result.done.length);
          toast(done.text, 'success', done.action);
        }
        if (result.limit) { this.upgrade = result.limit; this.upgradeOpen = true; }
        this.refreshWorkspace();
        await this.load();
      } catch (error) { this.limited(error); } finally { this.busy = false; }
    },
    dragStart(row, event) {
      this.dragId = row.id;
      event.dataTransfer.effectAllowed = 'move';
      try { event.dataTransfer.setData('text/plain', String(row.id)); } catch (error) { /* old browsers */ }
    },
    drop(target) {
      const from = this.published.findIndex(row => row.id === this.dragId);
      const to = this.published.findIndex(row => row.id === target.id);
      this.dragId = null;
      this.overId = null;
      if (from < 0 || to < 0 || from === to) return;
      const list = this.published.slice();
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      this.published = list;
      this.saveOrder();
    },
    shift(index, step) {
      const list = this.published.slice();
      const [moved] = list.splice(index, 1);
      list.splice(index + step, 0, moved);
      this.published = list;
      this.saveOrder();
    },
    async saveOrder() {
      try {
        await post('/brands/' + this.brandId + '/testimonials/order', { ids: this.published.map(row => row.id) });
        toast('Order saved.');
      } catch (error) { toast(message(error), 'error'); }
    }
  }
};
</script>

<style scoped>
.tv-section{margin-bottom:36px}
.tv-n{display:inline-grid;place-items:center;min-width:28px;height:24px;padding:0 8px;margin-left:6px;border-radius:999px;background:#F3EEE6;
  font:700 13px var(--font-body);color:#4F4A45;vertical-align:middle}
.tv-live-head{align-items:flex-end;flex-wrap:wrap}
.tv-meter{width:min(320px,100%)}
.tv-none{padding:22px;color:#4F4A45}
.tv-list{list-style:none;margin:0;padding:0;display:grid;gap:8px}
.tv-item{display:flex;align-items:center;gap:14px;padding:14px 16px;border:1px solid #E6DED2;border-radius:14px;background:#fff}
.tv-item.live{cursor:grab}
.tv-item.dragging{opacity:.5}
.tv-item.over{border-color:#B4472B;box-shadow:0 0 0 3px rgba(180,71,43,.15)}
.tv-grip{color:#8C8378;font-size:22px}
.tv-pos{display:inline-grid;place-items:center;width:28px;height:28px;flex:0 0 28px;border-radius:50%;background:#F3EEE6;font:700 13px var(--font-body);color:#4F4A45}
.tv-body{flex:1;min-width:0}
.tv-text{margin:0 0 4px;font:500 16px/1.5 var(--font-display)!important;color:#1F2328}
.tv-hidden{display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;margin:8px 0 0}
.tv-actions{flex:0 0 auto}
@media(max-width:700px){
  .tv-item{flex-wrap:wrap}
  .tv-grip{display:none}
  .tv-actions{width:100%;justify-content:flex-end}
}
</style>
